import React, {useState, useEffect, useMemo} from 'react';
import {t} from 'i18next';
import {Typography, Box} from '@mui/material';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import './CustomizeReviewerProgressWidget.scss';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import {WidgetsHeader} from '../widgets-header/WidgetsHeader';
import {WidgetsCustomizationReviewerSection} from '../widgets-customization-reviewer-section/WidgetsCustomizationReviewerSection';
import {
  useReviewerList,
  useSaveReviewProgress,
} from '../../../../hooks/dashboard/useDashboard';
import {useQueryClient} from '@tanstack/react-query';
import {
  AssignmentRPW,
  Reviewer,
  ReviewerProgressData,
  ReviewerProgressFilterDataRPW,
} from '../../dashboardTypes';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {useAssignmentsList} from '../../../../hooks/productions/useProductions';
import {formatAssignmentsTreeData} from '../../../../utils/formatTagTreeData';

type ReviewerOption = 'todoDocs' | 'completeDocs' | 'specificReviewers';

type CustomizeReviewerProgressWidgetProps = {
  showDrawer: boolean;
  handleDrawer: (value: React.SetStateAction<boolean>) => void;
  initialData: ReviewerProgressFilterDataRPW;
  onSuccess: (newData: ReviewerProgressData) => void;
};
const toApiReviewProgress = (
  ui: ReviewerProgressFilterDataRPW
): ReviewerProgressFilterDataRPW => ({
  ...ui,
});

export const CustomizeReviewerProgressWidget: React.FC<
  CustomizeReviewerProgressWidgetProps
> = ({showDrawer, handleDrawer, initialData, onSuccess}) => {
  const getInitialReviewerOption = (
    data: ReviewerProgressFilterDataRPW
  ): ReviewerOption => {
    if (data.reviewers && data.reviewers.length > 0) {
      return 'specificReviewers';
    }
    return data.isCompleted === 1 ? 'completeDocs' : 'todoDocs';
  };

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const {data: assignmentData} = useAssignmentsList(
    Number(loginUserData?.securityGroupId)
  );
  const assignments = useMemo(() => {
    if (assignmentData?.length) {
      return formatAssignmentsTreeData(assignmentData);
    }
  }, [assignmentData]);
  const queryClient = useQueryClient();
  const [reviewerOption, setReviewerOption] = useState<ReviewerOption>(
    getInitialReviewerOption(initialData)
  );
  const [form, setForm] = useState<ReviewerProgressFilterDataRPW>(initialData);
  const [selectedReviewers, setSelectedReviewers] = useState<string[]>(
    (initialData.reviewers ?? []).map((r) => r.reviewerName)
  );
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<string[]>(
    (initialData.assignments ?? []).map((a) => String(a.assignmentID))
  );
  const {data: reviewerList, refetch} = useReviewerList();

  useEffect(() => {
    if (showDrawer) {
      refetch();
      setForm(initialData);
      setReviewerOption(getInitialReviewerOption(initialData));
      setSelectedReviewers(
        (initialData.reviewers ?? []).map((r) => r.reviewerName)
      );
      setSelectedAssignmentIds(
        (initialData.assignments ?? []).map((a) => String(a.assignmentID))
      );
    }
  }, [showDrawer, initialData, refetch]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      isCompleted: reviewerOption === 'completeDocs' ? 1 : 0,
    }));
  }, [reviewerOption]);

  const childNodes = useMemo(() => {
    return (
      (assignments?.flatMap((p) => p.children ?? []) as Array<
        Required<TreeViewBaseItem<ExtendedTreeItemProps>>
      >) ?? []
    );
  }, []);
  const selectedAssignments: AssignmentRPW[] = useMemo(() => {
    return childNodes
      .filter((child) => selectedAssignmentIds.includes(String(child.id)))
      .map((child) => ({
        assignmentID: parseInt(String(child.id).replace(/\D/g, ''), 10) || 0,
        assignmentName: String(child.label),
      }));
  }, [childNodes, selectedAssignmentIds]);
  const handleCheckboxChange = (name: string) => {
    setSelectedReviewers((prev) =>
      prev.includes(name) ? prev.filter((r) => r !== name) : [...prev, name]
    );
  };

  const handleSelectedAssignments = (ids: string[] | null) => {
    setSelectedAssignmentIds(ids ?? []);
  };

  const {mutateAsync: saveReviewProgress, isPending: isSaving} =
    useSaveReviewProgress(() => {
      queryClient.invalidateQueries({queryKey: ['reviewerList']});
      handleDrawer(false);
    });

  const handleSave = async () => {
    const allReviewers: Reviewer[] = reviewerList ?? [];
    const reviewersForApi: Reviewer[] = allReviewers.filter((r) =>
      selectedReviewers.includes(r.reviewerName)
    );

    const payload: ReviewerProgressFilterDataRPW = {
      ...form,
      reviewers: reviewersForApi,
      assignments: selectedAssignments,
      reviewersName: selectedReviewers.join(', '),
      reveiwerName:
        selectedReviewers.length > 0 ? selectedReviewers[0] : form.reveiwerName,
      isDataUpdate: true,
    };

    const apiPayload = toApiReviewProgress(payload);
    saveReviewProgress(apiPayload, {
      onSuccess: (data: ReviewerProgressData) => {
        onSuccess(data);
      },
    });
  };

  const isReviewerOption = (v: string): v is ReviewerOption =>
    v === 'todoDocs' || v === 'completeDocs' || v === 'specificReviewers';
  const handleReviewerOptionChange = (value: string) => {
    if (!isReviewerOption(value)) return;
    if (
      value === 'specificReviewers' &&
      selectedReviewers.length === 0 &&
      (initialData.reviewers?.length ?? 0) > 0
    ) {
      setSelectedReviewers(
        (initialData.reviewers ?? []).map((r) => r.reviewerName)
      );
    } else {
      setSelectedReviewers([]);
    }

    setReviewerOption(value);
  };
  const contentBody = (
    <Box
      className="customize-reviewer-progress-widget-container"
      data-testid="crpgw-root"
      data-reviewer-option={reviewerOption}
      data-is-saving={String(isSaving)}
    >
      <Box data-testid="crpgw-header-wrapper">
        <WidgetsHeader
          mainTitle={t('dashboard.widgetCustomization.title')}
          subTitle={t('dashboard.widgetCustomization.reviewerProgress.title')}
          isCancelDisabled={false}
          onSave={handleSave}
          onCancel={() => handleDrawer(false)}
          isSaveDisabled={isSaving}
        />
      </Box>

      <Box className="content-box" data-testid="crpgw-content-box">
        <Box data-testid="crpgw-reviewer-section-wrapper">
          <WidgetsCustomizationReviewerSection
            reviewerOption={reviewerOption}
            onReviewerOptionChange={handleReviewerOptionChange}
            reviewers={reviewerList ?? []}
            selectedReviewers={selectedReviewers}
            onToggleReviewer={handleCheckboxChange}
          />
        </Box>

        <Typography
          className="typo-text subhead4_size"
          data-testid="crpgw-select-assignment-text"
        >
          {t(
            'dashboard.widgetCustomization.reviewerProductivity.selectAssignments'
          )}
        </Typography>

        {assignments && (
          <Box className="treeViewWrap" data-testid="crpgw-tree-view-wrap">
            <ArrTreeView
              itemsList={assignments}
              selectedItem={''}
              allowGlobalSelect={false}
              treeSelectedItems={selectedAssignmentIds}
              multipleSelection={true}
              isShowCountLabel={true}
              isShowCheckbox={true}
              showActions={true}
              customContentHeight={56}
              setSelectedItem={handleSelectedAssignments}
              defaultExpandedIds={
                selectedAssignmentIds.length
                  ? ['-1', ...selectedAssignmentIds]
                  : []
              }
            />
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {showDrawer && (
        <ArrDrawer
          showButton={false}
          isOpen={showDrawer}
          size="small"
          side="right"
          onClose={() => handleDrawer(false)}
          showHeader={false}
        >
          <Box data-testid="crpgw-drawer">{contentBody}</Box>
        </ArrDrawer>
      )}
    </>
  );
};
